import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import {
  SparklesIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface TourStep {
  targetSelector: string;
  title: string;
  badge: string;
  phrases: string[];
}

const ALL_TOUR_STEPS: TourStep[] = [
  {
    targetSelector: '[data-tour="organizacao"]',
    title: 'Organização',
    badge: 'Estrutura & Pessoas',
    phrases: [
      'É muito bom ter você por aqui!',
      'Nesse menu, você irá acompanhar suas movimentações, o organograma desta empresa, admissões, demissões e candidatos agendados.',
      'Ah, você verá também detalhes de cadastro dos colaboradores e visualizará quem está sem data do último exame para atualizar em massa sem perder prazos.',
    ],
  },
  {
    targetSelector: '[data-tour="agendar-exames"]',
    title: 'Agendar e Acompanhar Exames',
    badge: 'Agendamento Rápido',
    phrases: [
      'Faça seus agendamentos de forma simples e rápida.',
      'Mande os links pelo WhatsApp para os colaboradores escolherem as clínicas mais próximas deles, ou escolha você mesmo a clínica desejada.',
      'Acompanhe todos os agendamentos realizados, envie lembretes, faça reagendamentos, pegue guias e consulte os resultados.',
    ],
  },
  {
    targetSelector: '[data-tour="compliance"]',
    title: 'Compliance e Regulamentação',
    badge: 'Laudos & Contratos',
    phrases: [
      'Sua conformidade de SST centralizada em um só lugar.',
      'Renove sua procuração conosco e acompanhe sua vigência em tempo real.',
      'Baixe o nosso contrato assinado e todos os seus laudos regulatórios produzidos até hoje em uma única central.',
    ],
  },
  {
    targetSelector: '[data-tour="aprovar-candidatos"]',
    title: 'Aprovar Candidatos',
    badge: 'Admissões & eSocial',
    phrases: [
      'Sabe aquele agendamento que você não sabe se vai virar contratação e depois dá um trabalhão para informar?',
      'Pois é, isso acabou! Cadastre as admissões e gerencie a aprovação ou reprovação em um clique.',
      'Mas cuidado: lembre sempre de aprovar ou reprová-los no prazo ;)',
    ],
  },
  {
    targetSelector: '[data-tour="periodicos"]',
    title: 'Periódicos a Vencer',
    badge: 'Controle Preventivo',
    phrases: [
      'Quem precisa fazer exame e quando?',
      'Pronto! Clica aqui e veja tudo de forma fácil, preventiva e sem sofrer.',
    ],
  },
  {
    targetSelector: '[data-tour="usuarios"]',
    title: 'Usuários da Empresa',
    badge: 'Gestão de Acessos',
    phrases: [
      'Controle total sobre quem acessa o portal da sua empresa.',
      'Você pode criar novos acessos e solicitar alterações diretamente por este menu.',
    ],
  },
  {
    targetSelector: '[data-tour="select-empresa"]',
    title: 'Seletor de Empresas',
    badge: 'Múltiplos CNPJs',
    phrases: [
      'Ah, você tem mais de uma empresa no cadastro!',
      'Cuidado: aqui você visualiza as estruturas de forma separada para não ter confusão.',
      'Lembre sempre de mudar aqui no topo quando quiser alternar de uma empresa para outra.',
    ],
  },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const { empresa, empresas } = useEmpresa();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [activeSteps, setActiveSteps] = useState<TourStep[]>([]);
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  // Estados do Typewriter com ciclo de frases (digita -> pausa -> apaga -> próxima frase)
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const storageKey = `sst_customer_onboarding_v1_${user?.id || 'default'}`;
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calcula apenas os passos que a empresa ativa realmente possui contratados/visíveis
  const computeActiveSteps = useCallback(() => {
    return ALL_TOUR_STEPS.filter((step) => {
      // Se for o passo de troca de empresa, apenas se tiver mais de uma empresa cadastrada
      if (step.targetSelector === '[data-tour="select-empresa"]') {
        return Boolean(empresas && empresas.length > 1);
      }
      // Para os módulos, verifica se o elemento foi renderizado no DOM para a empresa atual
      const el = document.querySelector(step.targetSelector);
      return el !== null;
    });
  }, [empresas]);

  // Inicialização: Somente desktop e somente no primeiro acesso
  useEffect(() => {
    const isDesktop = window.innerWidth >= 1024;
    let hasCompleted: string | null = null;
    try {
      hasCompleted = localStorage.getItem(storageKey);
    } catch {
      hasCompleted = null;
    }

    if (isDesktop && !hasCompleted) {
      // Delay para os cards e permissões da empresa carregarem no DOM
      const timer = setTimeout(() => {
        const steps = computeActiveSteps();
        if (steps.length > 0) {
          setActiveSteps(steps);
          setIsOpen(true);
          setCurrentStep(0);
          setPhraseIndex(0);
        }
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [storageKey, computeActiveSteps, empresa?.id]);

  // Evento global para permitir rever o tour quando quiser
  useEffect(() => {
    const handleRestartTour = () => {
      if (window.innerWidth < 1024) {
        alert('O tour interativo está otimizado para telas desktop.');
        return;
      }
      const steps = computeActiveSteps();
      if (steps.length === 0) {
        alert('Nenhum módulo disponível para apresentação nesta empresa.');
        return;
      }
      setActiveSteps(steps);
      setIsOpen(true);
      setCurrentStep(0);
      setPhraseIndex(0);
    };

    window.addEventListener('restartCustomerTour', handleRestartTour);
    return () => window.removeEventListener('restartCustomerTour', handleRestartTour);
  }, [computeActiveSteps]);

  // Se trocar de empresa enquanto o tour estiver aberto, recalcula os passos
  useEffect(() => {
    if (isOpen) {
      const steps = computeActiveSteps();
      if (steps.length === 0) {
        setIsOpen(false);
      } else {
        setActiveSteps(steps);
        setCurrentStep(0);
        setPhraseIndex(0);
      }
    }
  }, [empresa?.id, isOpen, computeActiveSteps]);

  const finishTour = useCallback(() => {
    setIsOpen(false);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // Silently continue se cookies/localStorage estiverem bloqueados
    }
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    // Retorna ao topo e re-executa a animação de apresentação dos cards do Dashboard
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('tourFinishedAnimateDashboard'));
    }, 200);
  }, [storageKey]);

  // Tecla ESC para sair e Setas para navegar
  useEffect(() => {
    if (!isOpen || activeSteps.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        finishTour();
      } else if (e.key === 'ArrowRight') {
        if (currentStep < activeSteps.length - 1) {
          setCurrentStep((prev) => prev + 1);
          setPhraseIndex(0);
        } else {
          finishTour();
        }
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        setCurrentStep((prev) => prev - 1);
        setPhraseIndex(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, activeSteps.length, finishTour]);

  // Atualiza posição do elemento focado com proteção
  const updateTargetPosition = useCallback(() => {
    if (!isOpen || activeSteps.length === 0) return;
    const step = activeSteps[currentStep];
    if (!step) return;

    const el = document.querySelector(step.targetSelector) as HTMLElement | null;
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [isOpen, currentStep, activeSteps]);

  // Scroll Inteligente: Centraliza o elemento exatamente na Janela Segura Visível
  // (entre o topo do Header e o topo do HUD na base), garantindo ZERO risco de corte ou sobreposição.
  const scrollToTargetElement = useCallback((step: TourStep) => {
    const el = document.querySelector(step.targetSelector) as HTMLElement | null;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
    const absoluteElementTop = currentScrollY + rect.top;

    const winHeight = window.innerHeight;
    const isCompact = winHeight < 840;
    const headerHeight = 76; // altura de respiro para o Header fixo
    const hudHeight = isCompact ? 215 : 255;

    // Se for o seletor de empresa (que fica no topo do Header)
    if (step.targetSelector === '[data-tour="select-empresa"]') {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      return;
    }

    // Para cards normais do dashboard:
    // Em telas compactas ou quando o HUD ancorar na base, a zona livre é entre o Header e o HUD
    const bottomReserve = isCompact ? (hudHeight + 24) : 80;
    const safeZoneHeight = Math.max(180, winHeight - headerHeight - bottomReserve);

    // Centraliza o card com precisão matemática na janela visível desobstruída
    const idealElementTopInViewport = headerHeight + Math.max(12, (safeZoneHeight - rect.height) / 2);
    const targetScrollY = Math.max(0, absoluteElementTop - idealElementTopInViewport);

    try {
      window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, targetScrollY);
    }
  }, []);

  // Transição entre passos do tour com scroll seguro
  useEffect(() => {
    if (!isOpen || activeSteps.length === 0) return;

    const step = activeSteps[currentStep];
    if (!step) return;

    scrollToTargetElement(step);

    // Timer para recalcular a posição após a conclusão do scroll suave
    const timer = setTimeout(updateTargetPosition, 420);
    return () => clearTimeout(timer);
  }, [isOpen, currentStep, activeSteps, scrollToTargetElement, updateTargetPosition]);

  // Listener de Scroll e Resize em alta performance (60fps via requestAnimationFrame)
  useEffect(() => {
    if (!isOpen) return;

    let rafId: number | null = null;
    const handleScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setWindowDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
        updateTargetPosition();
      });
    };

    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize);
    };
  }, [isOpen, updateTargetPosition]);

  // Ciclo Inteligente de Digitação:
  // 1. Digita a frase atual com cadência humana (35ms ~ 45ms por char, pausas em pontuação)
  // 2. Ao terminar a frase:
  //    - Se NÃO for a última frase: aguarda tempo de leitura (~2s), apaga suavemente e passa para a próxima frase!
  //    - Se for a última frase: permanece na tela com o cursor pulsante.
  useEffect(() => {
    if (!isOpen || activeSteps.length === 0) return;

    const step = activeSteps[currentStep];
    if (!step || !step.phrases || step.phrases.length === 0) return;

    const currentPhrase = step.phrases[phraseIndex] || step.phrases[0];
    const isLastPhrase = phraseIndex === step.phrases.length - 1;

    setDisplayedText('');
    setIsTyping(true);

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    let charIndex = 0;

    // Função para apagar a frase antes da próxima
    const eraseCurrentPhrase = () => {
      let currentLength = currentPhrase.length;

      const deleteStep = () => {
        if (currentLength > 0) {
          currentLength = Math.max(0, currentLength - 2); // apaga rápido em blocos de 2 chars
          setDisplayedText(currentPhrase.slice(0, currentLength));
          typingTimerRef.current = setTimeout(deleteStep, 15);
        } else {
          // Passa para a próxima frase
          setPhraseIndex((prev) => prev + 1);
        }
      };

      typingTimerRef.current = setTimeout(deleteStep, 15);
    };

    // Função para digitar caractere por caractere
    const typeNextChar = () => {
      if (charIndex < currentPhrase.length) {
        const nextChar = currentPhrase[charIndex];
        setDisplayedText(currentPhrase.slice(0, charIndex + 1));
        charIndex++;

        // Velocidade cadenciada: pausas em vírgulas e pontos
        let delay = 35;
        if (nextChar === '.' || nextChar === '!' || nextChar === '?') {
          delay = 240;
        } else if (nextChar === ',' || nextChar === ';') {
          delay = 120;
        }

        typingTimerRef.current = setTimeout(typeNextChar, delay);
      } else {
        // Frase concluída
        setIsTyping(false);

        // Se houver mais frases neste passo, aguarda leitura e apaga para iniciar a próxima
        if (!isLastPhrase) {
          const readingPause = Math.max(1800, currentPhrase.length * 30);
          typingTimerRef.current = setTimeout(eraseCurrentPhrase, readingPause);
        }
      }
    };

    // Inicia a digitação da frase
    typingTimerRef.current = setTimeout(typeNextChar, 250);

    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, [isOpen, currentStep, phraseIndex, activeSteps]);

  // Se o usuário clicar no texto, exibe a frase completa imediatamente
  const handleFastForward = () => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    const step = activeSteps[currentStep];
    if (step) {
      const currentPhrase = step.phrases[phraseIndex] || step.phrases[0];
      setDisplayedText(currentPhrase);
      setIsTyping(false);
    }
  };

  const handleNext = () => {
    if (currentStep < activeSteps.length - 1) {
      setCurrentStep((prev) => prev + 1);
      setPhraseIndex(0);
    } else {
      finishTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      setPhraseIndex(0);
    }
  };

  if (!isOpen || activeSteps.length === 0) return null;

  const currentStepData = activeSteps[currentStep];
  const isLastStep = currentStep === activeSteps.length - 1;
  const isCompanyStep = currentStepData?.targetSelector === '[data-tour="select-empresa"]';
  const isCompactHeight = windowDimensions.height < 840;

  // Detecta se o elemento focado rolou para fora da área visível (scroll manual acidental)
  const isElementOutOfView = targetRect
    ? targetRect.bottom < 80 || targetRect.top > windowDimensions.height - 80
    : false;

  // Cálculo de posicionamento do HUD Flutuante do Assistente com Docking Inteligente
  const popoverPositionStyle: React.CSSProperties = (() => {
    const windowWidth = windowDimensions.width;
    const windowHeight = windowDimensions.height;
    const popoverWidth = Math.min(410, windowWidth - 32);

    if (!targetRect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${popoverWidth}px`,
      };
    }

    // 1. Se for o seletor de empresa (no topo absoluto do Header): posiciona logo abaixo
    if (isCompanyStep) {
      let left = targetRect.left + targetRect.width / 2 - popoverWidth / 2;
      if (left < 16) left = 16;
      if (left + popoverWidth > windowWidth - 16) {
        left = windowWidth - popoverWidth - 16;
      }
      return {
        top: `${targetRect.bottom + 14}px`,
        left: `${left}px`,
        width: `${popoverWidth}px`,
        transform: 'translateY(0)',
      };
    }

    // 2. Em telas compactas (laptops 1366x768 ou alturas < 840px): Docking Centralizado na Base
    // O card fica 100% visível na janela desobstruída superior e o HUD fica no cockpit inferior
    if (isCompactHeight) {
      return {
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: `${popoverWidth}px`,
      };
    }

    // 3. Em telas altas (monitores grandes 1080p+): posiciona adjacente ao card
    const hudHeightEst = 220;
    const spaceBelow = windowHeight - targetRect.bottom;
    const spaceAbove = targetRect.top;

    const placeBelow = spaceBelow >= hudHeightEst + 24 || spaceAbove < hudHeightEst + 24;

    const top = placeBelow ? targetRect.bottom + 14 : targetRect.top - 14;
    const transform = placeBelow ? 'translateY(0)' : 'translateY(-100%)';

    let left = targetRect.left + targetRect.width / 2 - popoverWidth / 2;
    if (left < 16) left = 16;
    if (left + popoverWidth > windowWidth - 16) {
      left = windowWidth - popoverWidth - 16;
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
      width: `${popoverWidth}px`,
      transform,
    };
  })();

  return (
    <div className="fixed inset-0 z-50 overflow-hidden font-sans select-none">
      {/* 1. Backdrop escuro com recorte Spotlight via Box-Shadow suave */}
      {targetRect && (
        <div
          className="fixed rounded-2xl pointer-events-none transition-all duration-300 ease-out"
          style={{
            top: `${targetRect.top - 6}px`,
            left: `${targetRect.left - 6}px`,
            width: `${targetRect.width + 12}px`,
            height: `${targetRect.height + 12}px`,
            boxShadow: '0 0 0 9999px rgba(3, 14, 9, 0.82), 0 0 30px rgba(52, 211, 153, 0.4)',
            border: '2px solid rgba(52, 211, 153, 0.85)',
          }}
        >
          <div className="absolute inset-0 rounded-2xl ring-4 ring-emerald-400/20 animate-pulse pointer-events-none" />
        </div>
      )}

      {!targetRect && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-[2px] transition-opacity duration-300" />
      )}

      {/* 2. HUD Flutuante do Assistente com Design Glassmorphism Dark Moderno */}
      <div
        className="fixed z-50 transition-all duration-300 ease-out"
        style={popoverPositionStyle}
      >
        <div className="bg-[#051810]/95 backdrop-blur-2xl border border-emerald-500/35 rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.85)] p-4 sm:p-5 text-white relative overflow-hidden ring-1 ring-emerald-500/20">
          {/* Linha de acento neon no topo do HUD */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-500" />

          {/* Cabeçalho do HUD */}
          <div className="flex items-center justify-between gap-2.5 mb-2.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-[0_0_10px_rgba(52,211,153,0.3)]">
                <SparklesIcon className="w-3.5 h-3.5" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-950/80 px-2.5 py-0.5 rounded-full border border-emerald-500/30">
                {currentStepData.badge}
              </span>
              <span className="text-[11px] font-mono text-emerald-400/70">
                {currentStep + 1} de {activeSteps.length}
              </span>

              {/* Botão para focar o card caso o usuário role a tela para longe */}
              {isElementOutOfView && (
                <button
                  type="button"
                  onClick={() => scrollToTargetElement(currentStepData)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/35 transition-all cursor-pointer animate-pulse"
                  title="Trazer o card de volta para a visualização"
                >
                  <ArrowPathIcon className="w-3 h-3" />
                  <span>Focar card</span>
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={finishTour}
              className="text-emerald-400/60 hover:text-emerald-200 hover:bg-emerald-900/40 p-1 rounded-lg transition-colors cursor-pointer"
              title="Fechar tutorial (ESC)"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Título do Módulo */}
          <h3 className="text-sm sm:text-base font-extrabold text-white tracking-tight mb-2 flex items-center justify-between">
            <span>{currentStepData.title}</span>
            {currentStepData.phrases.length > 1 && (
              <span className="text-[10px] font-mono font-normal text-emerald-400/60">
                Parte {phraseIndex + 1}/{currentStepData.phrases.length}
              </span>
            )}
          </h3>

          {/* Área de Digitação da Frase Atual */}
          <div
            onClick={handleFastForward}
            className="cursor-pointer group min-h-[76px] bg-[#020d07]/80 hover:bg-[#020d07]/95 border border-emerald-900/60 rounded-xl p-3 sm:p-3.5 transition-all mb-3 relative flex flex-col justify-between"
            title="Clique para acelerar o texto"
          >
            <p className="text-xs sm:text-[13px] text-emerald-50/95 leading-relaxed font-normal">
              {displayedText}
              {isTyping && (
                <span className="inline-block w-1.5 h-3.5 ml-1 bg-emerald-400 animate-pulse align-middle shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              )}
            </p>

            <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-emerald-950/60">
              <span className="text-[10px] text-emerald-400/50 group-hover:text-emerald-400/80 transition-colors">
                • Clique para acelerar
              </span>
              {/* Mini dots das frases */}
              <div className="flex items-center space-x-1">
                {currentStepData.phrases.map((_, idx) => (
                  <span
                    key={idx}
                    className={`inline-block rounded-full transition-all duration-300 ${
                      idx === phraseIndex
                        ? 'w-3.5 h-1 bg-emerald-400'
                        : idx < phraseIndex
                        ? 'w-1 h-1 bg-emerald-600'
                        : 'w-1 h-1 bg-emerald-950'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Rodapé com Navegação e Progresso Segmentado Futurista */}
          <div className="space-y-2.5">
            {/* Barra de Progresso Segmentada dos Módulos Ativos */}
            <div className="flex items-center gap-1.5 w-full">
              {activeSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    idx === currentStep
                      ? 'flex-1 bg-gradient-to-r from-emerald-400 to-teal-300 shadow-[0_0_8px_rgba(52,211,153,0.6)]'
                      : idx < currentStep
                      ? 'w-3 bg-emerald-600/80'
                      : 'w-3 bg-emerald-950'
                  }`}
                />
              ))}
            </div>

            {/* Ações */}
            <div className="flex items-center justify-between pt-0.5">
              <button
                type="button"
                onClick={finishTour}
                className="text-xs font-semibold text-emerald-400/60 hover:text-emerald-200 transition-colors cursor-pointer"
              >
                Pular tour
              </button>

              <div className="flex items-center gap-2">
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="px-3 py-1.5 rounded-xl border border-emerald-800/60 bg-emerald-950/40 text-xs font-semibold text-emerald-300 hover:bg-emerald-900/50 hover:text-white transition-all flex items-center gap-1 cursor-pointer"
                  >
                    <ChevronLeftIcon className="w-3.5 h-3.5" />
                    <span>Voltar</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleNext}
                  className="px-4 py-1.5 rounded-xl text-slate-950 font-bold text-xs bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-400 hover:brightness-110 shadow-[0_0_15px_rgba(16,185,129,0.35)] hover:shadow-[0_0_20px_rgba(16,185,129,0.55)] transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
                >
                  <span>{isLastStep ? 'Concluir' : 'Próximo'}</span>
                  {isLastStep ? (
                    <CheckIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                  ) : (
                    <ChevronRightIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
